/**
 * Utility functions for visit colors
 */

// Default color palette for visits
export const VISIT_COLORS = [
  '#3174ad', // Blue
  '#e91e63', // Pink
  '#9c27b0', // Purple
  '#673ab7', // Deep Purple
  '#00bcd4', // Cyan
  '#009688', // Teal
  '#4caf50', // Green
  '#ff9800', // Orange
  '#ff5722', // Deep Orange
  '#795548', // Brown
  '#607d8b', // Blue Grey
  '#f44336', // Red
];

// Lighter palette for better readability
export const VISIT_COLORS_LIGHT = [
  '#5C9BD5', // Light Blue
  '#ED7D95', // Light Pink
  '#B85FC1', // Light Purple
  '#8B6BBF', // Light Deep Purple
  '#4DD0E1', // Light Cyan
  '#4DB6AC', // Light Teal
  '#81C784', // Light Green
  '#FFB74D', // Light Orange
  '#FF8A65', // Light Deep Orange
  '#A1887F', // Light Brown
  '#90A4AE', // Light Blue Grey
  '#E57373', // Light Red
];

/**
 * Get color based on visit index (cyclic)
 */
export const getVisitColorByIndex = (index: number, useLightColors = false): string => {
  const palette = useLightColors ? VISIT_COLORS_LIGHT : VISIT_COLORS;
  return palette[index % palette.length];
};

/**
 * Generate a consistent color based on a string (e.g., service name or client name)
 * Uses a simple hash function to ensure the same string always gets the same color
 */
export const getVisitColorByString = (str: string, useLightColors = false): string => {
  const palette = useLightColors ? VISIT_COLORS_LIGHT : VISIT_COLORS;

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }

  const index = Math.abs(hash) % palette.length;
  return palette[index];
};

/**
 * Get color for visit based on service name
 * If serviceName is provided, always returns the same color for that service
 * Otherwise falls back to index-based color
 */
export const getVisitColor = (
  index: number,
  serviceName?: string,
  useLightColors = false,
): string => {
  if (serviceName) {
    return getVisitColorByString(serviceName, useLightColors);
  }
  return getVisitColorByIndex(index, useLightColors);
};

/**
 * Get contrasting text color (white or black) based on background color
 */
export const getContrastColor = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return white for dark colors, black for light colors
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};
